import { parse, serialize } from 'parse5';
import type { TextNode, Element, Node, ParentNode, Document } from 'parse5';

import { escapeRegExp } from '@pega/cosmos-react-core';

import type { EmailProps, EntityMatch, MatchRange } from '../Email.types';

const isParentNode = (node: Node | undefined): node is ParentNode =>
  !!node &&
  node.nodeName !== '#text' &&
  node.nodeName !== '#comment' &&
  node.nodeName !== '#documentType';

/**
 * Function to check if there is nesting in the given ranges
 * @param range1
 * @param range2
 */
const isNesting = (range1: MatchRange, range2: MatchRange): boolean => {
  return (
    (range1.start >= range2.start && range1.end <= range2.end) ||
    (range2.start >= range1.start && range2.end <= range1.end)
  );
};

/**
 * Function to check if there is an intersection in the ranges
 * @param range1
 * @param range2
 */
const isIntersecting = (range1: MatchRange, range2: MatchRange): boolean => {
  return (
    (range1.start > range2.start && range1.start <= range2.end && range1.end > range2.end) ||
    (range2.start > range1.start && range2.start <= range1.end && range2.end > range1.end)
  );
};

/**
 * Callback function to sort ranges
 * @param range1
 * @param range2
 */
const matchSorter = (range1: MatchRange, range2: MatchRange): number => {
  if (range1.start === range2.start) {
    return range2.end - range1.end;
  }

  return range1.start - range2.start;
};

/**
 * Recursive function to generate nested matches structure by inserting it at the right place
 * @param match
 * @param nestedMatches
 */
const insertIntoNestedMatches: (
  match: EntityMatch,
  nestedMatches: EntityMatch[]
) => EntityMatch[] = (match: EntityMatch, nestedMatches: EntityMatch[]): EntityMatch[] => {
  const updatedNestedMatches = [...nestedMatches];
  let isInserted = false;

  for (let index = 0; index < nestedMatches.length; index += 1) {
    const matchInArr = nestedMatches[index];

    if (isNesting(match, matchInArr)) {
      if (match.end - match.start <= matchInArr.end - matchInArr.start) {
        // The range of existing match in array is either larger or equal, so new match can be a child.
        if (matchInArr.children && matchInArr.children.length > 0) {
          // Recursive call
          const updatedChildren = insertIntoNestedMatches(match, matchInArr.children);
          // Update matchInArr in the newOverlapsArray
          matchInArr.children = updatedChildren;
        } else {
          matchInArr.children = [match];
        }
        updatedNestedMatches.splice(index, 1, matchInArr);
        isInserted = true;
        break;
      } else {
        // Replace the existing match and make it the child of current match
        updatedNestedMatches.splice(index, 1, { ...match, children: [matchInArr] });
        isInserted = true;
        break;
      }
    }

    if (isIntersecting(match, matchInArr)) {
      // Intersection
      // We are only keeping the first match and discarding the following match
      if (match.start < matchInArr.start) {
        updatedNestedMatches.splice(index, 1, match);
        isInserted = true;
      } else {
        isInserted = true;
      }
      break;
    }
  }

  if (!isInserted) {
    // Not nesting or intersecting
    updatedNestedMatches.push(match);
  }

  return updatedNestedMatches;
};

const getConsolidatedDescription = (match: EntityMatch) => {
  const consolidatedDescription: { [name: string]: Set<string> } = {};

  match.names?.forEach(name => {
    if (!consolidatedDescription[name]) {
      consolidatedDescription[name] = new Set();
    }
    consolidatedDescription[name].add(match.matchedText);
  });

  if (match.children && match.children.length > 0) {
    match.children.forEach(nestedMatch => {
      const nestMatchDesc = getConsolidatedDescription(nestedMatch);
      Object.keys(nestMatchDesc).forEach(key => {
        if (consolidatedDescription[key]) {
          consolidatedDescription[key] = new Set([
            ...consolidatedDescription[key],
            ...nestMatchDesc[key]
          ]);
        } else {
          consolidatedDescription[key] = nestMatchDesc[key];
        }
      });
    });
  }

  return consolidatedDescription;
};

const highlightEntitiesInTextNode = (
  textNode: TextNode,
  entityMapping: EmailProps['entityHighlightMapping']
) => {
  const matches: EntityMatch[] = [];
  // Find all matches
  entityMapping?.forEach(mapping => {
    const escapedValue = escapeRegExp(mapping.value);
    const regExp = new RegExp(`(?<=\\W|^)${escapedValue}(?=(\\W|$))`, 'ig');
    let res;
    // eslint-disable-next-line no-cond-assign
    while ((res = regExp.exec(textNode.value)) !== null) {
      matches.push({
        start: res.index,
        end: res.index + res[0].length,
        matchedText: res[0],
        ...mapping
      });
    }
  });

  if (matches.length === 0) {
    // Early exit as there are no matches
    const newSpanNode: Element = {
      nodeName: 'span',
      tagName: 'span',
      parentNode: textNode.parentNode,
      attrs: [],
      namespaceURI: '',
      childNodes: []
    };
    newSpanNode.childNodes.push(textNode);
    return newSpanNode;
  }

  matches.sort(matchSorter);

  // Handle nesting and intersections
  let nestedMatches: EntityMatch[] = [];
  matches.forEach(match => {
    nestedMatches = insertIntoNestedMatches(match, nestedMatches);
  });

  // Wrap matched ranges
  let lastIndex = 0;
  const updatedNodes: Node[] = [];
  nestedMatches.sort(matchSorter);
  nestedMatches.forEach(match => {
    if (match.start !== lastIndex) {
      const newSpanNode: Element = {
        nodeName: 'span',
        tagName: 'span',
        parentNode: textNode.parentNode,
        attrs: [],
        namespaceURI: '',
        childNodes: []
      };

      newSpanNode.childNodes.push({
        ...textNode,
        nodeName: '#text',
        value: textNode.value.substring(lastIndex, match.start),
        parentNode: newSpanNode
      });

      updatedNodes.push(newSpanNode);
    }

    const consolidatedDescription = getConsolidatedDescription(match);
    let consolidatedDescriptionArray;
    if (Object.entries(consolidatedDescription).length === 1) {
      consolidatedDescriptionArray = [`${Object.keys(consolidatedDescription)[0]}`];
    } else {
      consolidatedDescriptionArray = Object.entries(consolidatedDescription).map(([key, value]) => {
        return `${key}: ${[...value].join(', ')}`;
      });
    }

    const newMarkNode: Element = {
      nodeName: 'pega-email-entity',
      tagName: 'pega-email-entity',
      parentNode: textNode.parentNode,
      attrs: [
        { name: 'data-variant', value: `${match.variant}` },
        { name: 'data-entity-type', value: match.type },
        { name: 'data-description', value: JSON.stringify(consolidatedDescriptionArray) }
      ],
      namespaceURI: '',
      childNodes: []
    };

    newMarkNode.childNodes.push({
      nodeName: '#text',
      value: match.matchedText,
      parentNode: newMarkNode
    });

    updatedNodes.push(newMarkNode);

    lastIndex = match.end;
  });

  if (lastIndex !== textNode.value.length) {
    const newSpanNode: Element = {
      nodeName: 'span',
      tagName: 'span',
      parentNode: textNode.parentNode,
      attrs: [],
      namespaceURI: '',
      childNodes: []
    };

    newSpanNode.childNodes.push({
      ...textNode,
      nodeName: '#text',
      value: textNode.value.substring(lastIndex),
      parentNode: newSpanNode
    } as TextNode);

    updatedNodes.push(newSpanNode);
  }

  return updatedNodes;
};

const highlightEntitiesInNode = (
  node: Node,
  entityMapping: EmailProps['entityHighlightMapping']
) => {
  // Either call self recursively or call mark entities in text
  if (isParentNode(node) && node.childNodes?.length > 0) {
    // Iteratively mark entities to all child nodes
    let newChildNodes: Node[] = [];
    node.childNodes.forEach(childNode => {
      const res = highlightEntitiesInNode(childNode, entityMapping);
      if (Array.isArray(res)) {
        newChildNodes = [...newChildNodes, ...res];
      } else {
        newChildNodes.push(res as Node);
      }
    });
    return { ...node, childNodes: newChildNodes };
  }

  if (node.nodeName === '#text') {
    return highlightEntitiesInTextNode(node as TextNode, entityMapping);
  }

  return node;
};

/**
 * Function to highlight entities in a given html
 * @param html
 * @param entityMapping
 */
const highlightEntities = (
  html: string,
  entityMapping: EmailProps['entityHighlightMapping']
): string => {
  const document: Document = parse(html);

  const markedDocument = highlightEntitiesInNode(document, entityMapping);

  return serialize(markedDocument as Document);
};

export default highlightEntities;
